import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { CourseDetails, Lesson } from '../../hooks/useCourseApi';
import { useContact } from '../../hooks/useContact';
import axios from 'axios';
import { BookOpen, BookMarked } from 'lucide-react';
import EnrolledCourseCard from './EnrolledCourseCard';
import MyProgressStatsSection from './MyProgressStatsSection';
import { cardTheme } from '../../styles/cardTheme';
import {
  getStudentProgressStats,
  getStudentQuizProgress,
  getStudentVideoProgress,
} from '../../services/statisticsService';
import type { QuizProgressStats, StudentProgressStats, VideoProgressStats } from '../../types/statistics';

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  grade: string;
  description: string;
  imagePath: string;
  modificationDate: string;
  progress?: number; // Progress percentage (0-100)
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    courses: Course[];
  };
}

// Centralized request deduplication
const pendingRequests = new Map<string, Promise<any>>();

const EnrolledCourses: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10; // Updated to match API default
  const [overallProgress, setOverallProgress] = useState<VideoProgressStats | null>(null);
  const [quizProgress, setQuizProgress] = useState<QuizProgressStats | null>(null);
  const [studentStats, setStudentStats] = useState<StudentProgressStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState<Set<string>>(new Set());
  const [courseCurriculums, setCourseCurriculums] = useState<Map<string, CourseDetails>>(new Map());
  const [loadingCurriculums, setLoadingCurriculums] = useState<Set<string>>(new Set());
  const [expandedUnits, setExpandedUnits] = useState<Map<string, Set<number>>>(new Map());
  const { openWhatsApp, whatsappNumber } = useContact();
  
  // Helper function to construct course image URL
  const getCourseImageSrc = (course: Course) => {
    // If imagePath is null or undefined, use default image
    if (!course.imagePath) {
      return '/default-course.jpg';
    }
    
    if (course.imagePath) {
      // If it's already a full URL, use it as is
      if (course.imagePath.startsWith('http')) {
        return course.imagePath;
      }
      
      // Handle relative paths - remove leading slash to avoid double slashes
      let imagePath = course.imagePath;
      if (imagePath.startsWith('/')) {
        imagePath = imagePath.substring(1); // Remove leading slash
      }
      
      // Ensure BASE_URL ends with a slash
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
      return `${baseUrl}${imagePath}`;
    }
    // Fallback to a default image if no image path is provided
    return '/default-course.jpg';
  };

  useEffect(() => {
    fetchCourses();
  }, [currentPage, currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id) {
      fetchProgressStats();
    }
  }, [currentUser?.id]);

  const fetchProgressStats = async () => {
    if (!currentUser?.id) return;
    setLoadingStats(true);
    try {
      const [stats, video, quiz] = await Promise.all([
        getStudentProgressStats(currentUser.id),
        getStudentVideoProgress(currentUser.id),
        getStudentQuizProgress(currentUser.id),
      ]);
      setStudentStats(stats);
      setOverallProgress(video);
      setQuizProgress(quiz);
    } finally {
      setLoadingStats(false);
    }
  };

  // WhatsApp contact handler for support
  const handleWhatsAppSupport = useCallback(() => {
    if (whatsappNumber) {
      const message = 'مرحباً، أريد الاستفسار عن اشتراكاتي';
      openWhatsApp(message);
    }
  }, [whatsappNumber, openWhatsApp]);

  const fetchCourses = async () => {
    // Create a unique key for this request to prevent duplicates
    const requestKey = `courses_${currentUser?.id}_${currentPage}_${pageSize}`;
    
    // Check if there's already a pending request for this key
    if (pendingRequests.has(requestKey)) {
      console.log('Request already in progress, skipping duplicate call');
      return pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setError('لم يتم العثور على رمز المصادقة. الرجاء تسجيل الدخول مرة أخرى.');
          return;
        }

        console.log('Fetching enrolled courses:', { userId: currentUser?.id, page: currentPage });

        const response = await fetch(
          `${BASE_URL}api/Student/Student-Enrolled-Courses?studentId=${currentUser?.id}&pagenumber=${currentPage}&pagesize=${pageSize}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...getTenantHeaders()
            },
          }
        );

        if (!response.ok) {
        if (response.status === 401) {
          setError('انتهت صلاحية الجلسة. الرجاء تسجيل الدخول مرة أخرى.');
          navigate('/login');
          return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();
          
        if (data.success && data.data) {
            const coursesList = data.data.courses;
            setCourses(coursesList);
            setTotalPages(data.data.totalPages);
            setTotalCount(data.data.totalCount);
            
            // Fetch progress for each course
            await fetchCoursesProgress(coursesList);
        } else {
          setError(data.message || 'حدث خطأ أثناء جلب الباقات');
          setCourses([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('حدث خطأ أثناء جلب الباقات');
        setCourses([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    })();
    
    // Store the promise and clean it up when done
    pendingRequests.set(requestKey, requestPromise);
    
    try {
      await requestPromise;
    } finally {
      pendingRequests.delete(requestKey);
    }
  };

  // Fetch progress for enrolled courses
  const fetchCoursesProgress = async (coursesList: Course[]) => {
    if (!currentUser?.id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Fetch progress for all courses in parallel
      const progressPromises = coursesList.map(async (course) => {
        try {
          const response = await axios.get<{ success: boolean; data?: CourseDetails }>(
            `${BASE_URL}api/Course/tree-course-with-progress`,
            {
              params: { courseid: course.id },
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...getTenantHeaders()
              }
            }
          );

          if (response.data.success && response.data.data) {
            const courseDetails = response.data.data;
            
            // Calculate progress percentage
            let totalLessons = 0;
            let completedLessons = 0;
            
            courseDetails.units?.forEach(unit => {
              unit.lessons?.forEach(lesson => {
                totalLessons++;
                if ((lesson.type === 'Video' && lesson.isCompleted) || 
                    (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
                    (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
                    (lesson.type === 'File' && lesson.isFileOpened)) {
                  completedLessons++;
                }
              });
            });
            
            const progress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
            
            return { courseId: course.id, progress };
          }
        } catch (err) {
          console.error(`Error fetching progress for course ${course.id}:`, err);
          return { courseId: course.id, progress: 0 };
        }
        
        return { courseId: course.id, progress: 0 };
      });

      const progressResults = await Promise.all(progressPromises);
      
      // Update courses with progress
      setCourses(prevCourses => 
        prevCourses.map(course => {
          const progressData = progressResults.find(p => p.courseId === course.id);
          return {
            ...course,
            progress: progressData?.progress ?? 0
          };
        })
      );
    } catch (err) {
      console.error('Error fetching courses progress:', err);
    }
  };

  const handleViewCurriculum = (courseId: string) => {
    navigate(`/course/${courseId}/curriculum`);
  };

  // Fetch course curriculum
  const fetchCourseCurriculum = async (courseId: string, autoExpand: boolean = false) => {
    if (courseCurriculums.has(courseId)) {
      // Already loaded
      if (autoExpand && !expandedCourseId.has(courseId)) {
        setExpandedCourseId(prev => new Set(prev).add(courseId));
        // Expand all units by default
        const curriculum = courseCurriculums.get(courseId);
        if (curriculum) {
          const unitIds = curriculum.units.map((_, index) => index);
          setExpandedUnits(prev => {
            const newMap = new Map(prev);
            newMap.set(courseId, new Set(unitIds));
            return newMap;
          });
        }
      }
      return;
    }

    // Add to loading set
    setLoadingCurriculums(prev => new Set(prev).add(courseId));

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await axios.get<{ success: boolean; data?: CourseDetails }>(
        `${BASE_URL}api/Course/tree-course-with-progress`,
        {
          params: { courseid: courseId },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...getTenantHeaders()
          }
        }
      );

      if (response.data.success && response.data.data) {
        const curriculum = response.data.data;
        setCourseCurriculums(prev => {
          const newMap = new Map(prev);
          newMap.set(courseId, curriculum);
          return newMap;
        });
        
        if (autoExpand) {
          setExpandedCourseId(prev => new Set(prev).add(courseId));
          // Expand all units by default
          const unitIds = curriculum.units.map((_, index) => index);
          setExpandedUnits(prev => {
            const newMap = new Map(prev);
            newMap.set(courseId, new Set(unitIds));
            return newMap;
          });
        }
      }
    } catch (err) {
      console.error(`Error fetching curriculum for course ${courseId}:`, err);
    } finally {
      setLoadingCurriculums(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }
  };

  // Toggle curriculum dropdown
  const toggleCurriculum = (courseId: string) => {
    if (expandedCourseId.has(courseId)) {
      setExpandedCourseId(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    } else {
      fetchCourseCurriculum(courseId, true);
    }
  };

  // Toggle unit expansion
  const toggleUnit = (courseId: string, unitIndex: number) => {
    setExpandedUnits(prev => {
      const newMap = new Map(prev);
      const courseUnits = newMap.get(courseId) || new Set<number>();
      const newCourseUnits = new Set(courseUnits);
      
      if (newCourseUnits.has(unitIndex)) {
        newCourseUnits.delete(unitIndex);
      } else {
        newCourseUnits.add(unitIndex);
      }
      
      newMap.set(courseId, newCourseUnits);
      return newMap;
    });
  };

  // Get lesson type icon
  const getLessonTypeIcon = (lesson: Lesson) => {
    switch (lesson.type) {
      case 'Video':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Quiz':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'Assignment':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'File':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={
        embedded
          ? 'py-2 px-4 sm:px-6 lg:px-8'
          : 'min-h-screen bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 py-8 px-4 sm:px-6 lg:px-8'
      }
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {!embedded && (
        <div className="text-center mb-8 sm:mb-10">
          <span className={`inline-flex items-center gap-2 mb-4 ${cardTheme.badgeGold}`}>
            <BookMarked className="w-4 h-4" />
            مساحتك التعليمية
          </span>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${cardTheme.title}`}>كورساتي</h1>
          <p className={`text-base sm:text-lg max-w-xl mx-auto ${cardTheme.subtitle}`}>
            {totalCount > 0
              ? `عندك ${totalCount} ${totalCount === 1 ? 'كورس' : 'كورسات'} — كمّل من حيث وقفت`
              : 'اكتشف كورساتك المسجّلة وابدأ رحلة التعلم'}
          </p>

          <MyProgressStatsSection
            stats={studentStats}
            videoProgress={overallProgress}
            quizProgress={quizProgress}
            loading={loadingStats}
          />
        </div>
        )}

        {embedded && (
          <div className="mb-5 sm:mb-6">
            <MyProgressStatsSection
              stats={studentStats}
              videoProgress={overallProgress}
              quizProgress={quizProgress}
              loading={loadingStats}
            />
          </div>
        )}

        {loading ? (
          <div className="flex flex-col justify-center items-center min-h-[400px] gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-700" />
            <p className="text-sm text-green-800 font-medium">جاري تحميل كورساتك...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-700 bg-red-50 border border-red-200 rounded-2xl p-6">
            {error}
          </div>
        ) : courses.length === 0 ? (
          <div className={`text-center py-14 px-6 ${cardTheme.shell}`}>
            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${cardTheme.iconBox}`}>
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-green-900">لا توجد كورسات مسجّلة بعد</h3>
            <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">سجّل في كورس جديد وابدأ رحلتك مع نهج الأزهر</p>
            <Link
              to="/student-portal/browse"
              className={`inline-flex mt-6 items-center gap-2 px-6 py-2.5 rounded-xl ${cardTheme.btnPrimary}`}
            >
              تصفّح الكورسات
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:gap-6">
              {courses.map((course) => {
                const curriculum = courseCurriculums.get(course.id);
                const isExpanded = expandedCourseId.has(course.id);
                const isLoadingCurriculum = loadingCurriculums.has(course.id);
                const courseExpandedUnits = expandedUnits.get(course.id) || new Set<number>();

                return (
                  <EnrolledCourseCard
                    key={course.id}
                    course={course}
                    imageSrc={getCourseImageSrc(course)}
                    curriculum={curriculum}
                    isExpanded={isExpanded}
                    isLoadingCurriculum={isLoadingCurriculum}
                    expandedUnitIndexes={courseExpandedUnits}
                    onToggleCurriculum={() => toggleCurriculum(course.id)}
                    onToggleUnit={(unitIndex) => toggleUnit(course.id, unitIndex)}
                    onContinue={() => handleViewCurriculum(course.id)}
                    getLessonTypeIcon={getLessonTypeIcon}
                  />
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 mb-2 flex flex-wrap items-center justify-center gap-2 pb-16 sm:pb-0">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                    currentPage === 1
                      ? 'bg-green-50 text-gray-400 cursor-not-allowed border border-green-100'
                      : `${cardTheme.btnOutline} bg-white`
                  }`}
                >
                  السابق
                </button>
                <span className={`px-4 py-2 rounded-xl text-sm font-bold ${cardTheme.btnPrimary}`}>
                  {currentPage} من {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                    currentPage === totalPages
                      ? 'bg-green-50 text-gray-400 cursor-not-allowed border border-green-100'
                      : `${cardTheme.btnOutline} bg-white`
                  }`}
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating WhatsApp Button with Speech Bubble */}
      {!embedded && whatsappNumber && (
        <div className="fixed bottom-6 left-6 z-40 flex items-end gap-3">
          {/* Speech Bubble Message */}
          <div className="relative bg-white border border-gray-200 shadow-xl rounded-2xl p-4 max-w-64">
            <div className="text-gray-800 text-xs font-medium leading-relaxed">
              ازيك يا صديقي .. تواصل معانا لو قابلك أي مشكلة
            </div>
            {/* Speech bubble tail */}
          </div>

          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppSupport}
            className="group relative bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110"
          >
            {/* WhatsApp Icon */}
            <svg
              className="w-6 h-6"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>

            {/* Pulse Animation */}
            <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20"></div>
          </button>
        </div>
      )}
    </div>
  );
};

export default EnrolledCourses; 